import { NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { 
  LayoutDashboard, 
  Users, 
  UserCog, 
  LogOut, 
  Target,
  ChevronRight,
  Upload,
  CreditCard,
  CalendarDays,
  Ticket,
  KeyRound,
  Eye,
  EyeOff,
  Mail,
  Send,
  FileText,
  Workflow,
  Ban,
  Activity,
  BarChart3,
  Settings as SettingsIcon
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { formatErrorDetail } from "@/lib/format-error";
import NotificationBell from "./NotificationBell";

const Sidebar = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }
    if (passwordData.newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setIsChangingPassword(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/auth/change-password`,
        {
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Password changed successfully");
      setIsPasswordModalOpen(false);
      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (err) {
      toast.error(formatErrorDetail(err, "Failed to change password"));
    } finally {
      setIsChangingPassword(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const navItems = [
    {
      label: "Dashboard",
      icon: LayoutDashboard,
      path: "/dashboard"
    },
    {
      label: "Calendar",
      icon: CalendarDays,
      path: "/calendar"
    },
    {
      label: "Leads",
      icon: Users,
      path: "/leads"
    }
  ];

  // Add manager-only items (existing CRM features — keep manager-gated)
  if (user?.role === "manager") {
    navItems.push({
      label: "Uploads",
      icon: Upload,
      path: "/uploads"
    });
    navItems.push({
      label: "Team",
      icon: UserCog,
      path: "/users"
    });
    navItems.push({
      label: "Team Activity",
      icon: Activity,
      path: "/team-activity"
    });
    navItems.push({
      label: "Subscriptions",
      icon: CreditCard,
      path: "/subscriptions"
    });
    navItems.push({
      label: "Sales Dashboard",
      icon: BarChart3,
      path: "/sales-dashboard"
    });
  }
  // API Keys lives under Settings (super-admin only) rather than the main
  // sidebar. Day-to-day, managers connect via OAuth; API keys are kept
  // around for dev tools (MCP Inspector), scripts, and break-glass cases —
  // not something most users should see in the main nav.

  // Super admin only — Invite Codes + email infrastructure (Phases 1-3).
  // To later expose any of these to managers or reps, move the navItems.push
  // call out of this block into the manager block above (or above this whole
  // section to expose to anyone logged in). Pair with the matching backend
  // guard swap (require_super_admin → require_manager or no guard).
  if (user?.is_super_admin) {
    navItems.push({
      label: "Invite Codes",
      icon: Ticket,
      path: "/invite-codes"
    });
    navItems.push({
      label: "Email Tools",
      icon: Mail,
      path: "/email-tools"
    });
    navItems.push({
      label: "Sent Items",
      icon: Send,
      path: "/sent-items"
    });
    navItems.push({
      label: "Email Templates",
      icon: FileText,
      path: "/email-templates"
    });
    navItems.push({
      label: "Sequences",
      icon: Workflow,
      path: "/sequences"
    });
    navItems.push({
      label: "Suppressions",
      icon: Ban,
      path: "/suppressions"
    });
    navItems.push({
      label: "Settings",
      icon: SettingsIcon,
      path: "/settings"
    });
  }

  return (
    <aside 
      className="fixed left-0 top-0 h-screen w-64 bg-white border-r border-slate-100 flex flex-col"
      data-testid="sidebar"
    >
      {/* Logo & Org Name — sticky top */}
      <div className="p-6 border-b border-slate-100 shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-indigo-600 flex items-center justify-center">
              <Target className="w-5 h-5 text-white" />
            </div>
            <span 
              className="font-bold text-xl text-slate-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Storm Pad
            </span>
          </div>
          <NotificationBell />
        </div>
        {user?.org_name && (
          <div className="px-2 py-1 rounded bg-slate-100 text-xs font-medium text-slate-600 truncate">
            {user.org_name}
          </div>
        )}
      </div>

      {/* Navigation — scrollable middle */}
      <nav className="flex-1 min-h-0 overflow-y-auto p-4 space-y-1">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              `sidebar-item flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium ${
                isActive
                  ? "active bg-indigo-50 text-indigo-600"
                  : "text-slate-600 hover:bg-slate-50"
              }`
            }
            data-testid={`nav-${item.label.toLowerCase()}`}
          >
            <item.icon className="w-5 h-5" />
            {item.label}
          </NavLink>
        ))}
      </nav>

      {/* User Profile — sticky bottom */}
      <div className="p-4 border-t border-slate-100 shrink-0">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              className="w-full justify-start px-3 py-2 h-auto"
              data-testid="user-menu-trigger"
            >
              <div className="flex items-center gap-3 w-full">
                <Avatar className="h-9 w-9">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-indigo-100 text-indigo-700 text-sm font-medium">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-slate-900 truncate">
                    {user?.name}
                  </p>
                  <Badge 
                    variant="secondary" 
                    className={`text-xs ${
                      user?.role === "manager" 
                        ? "bg-amber-100 text-amber-700" 
                        : "bg-indigo-100 text-indigo-700"
                    }`}
                  >
                    {user?.role === "manager" ? "Manager" : "Sales Rep"}
                  </Badge>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium">{user?.name}</p>
              <p className="text-xs text-slate-500">{user?.email}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={() => setIsPasswordModalOpen(true)}
              data-testid="change-password-btn"
            >
              <KeyRound className="w-4 h-4 mr-2" />
              Change Password
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={onLogout}
              className="text-red-600"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Change Password Modal */}
      <Dialog open={isPasswordModalOpen} onOpenChange={setIsPasswordModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Current Password</Label>
              <div className="relative">
                <Input
                  id="currentPassword"
                  type={showPasswords.current ? "text" : "password"}
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  placeholder="Enter current password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPasswords.new ? "text" : "password"}
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  placeholder="Enter new password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showPasswords.confirm ? "text" : "password"}
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  placeholder="Confirm new password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button 
              onClick={handleChangePassword} 
              className="w-full bg-indigo-600 hover:bg-indigo-700"
              disabled={isChangingPassword}
            >
              {isChangingPassword ? "Changing..." : "Change Password"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </aside>
  );
};

export default Sidebar;
